/**
 * First-run vocabulary note — shown once, for a project that has no strategies
 * yet. It names the four words the wizard uses and points at the ⓘ for
 * everything else; dismissal is per dialog session (owner ruling: nothing is
 * persisted, the note simply stops appearing once the project has a strategy).
 */

import React from 'react';
import { Box, Button, Paper, Typography, alpha } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

export const FIRST_RUN_VOCABULARY =
  'Words used here — task: a goal your users come with · route: the funnel path for one task · '
  + 'variable: an answer the funnel collects · block: a group of screens. This note appears once; '
  + 'the ⓘ next to every step title opens the same guidance later.';

export interface FirstRunNoteProps {
  onDismiss: () => void;
}

export const FirstRunNote: React.FC<FirstRunNoteProps> = ({ onDismiss }) => (
  <Paper
    variant="outlined"
    data-testid={STRATEGY_LIST.wizardFirstRunNote}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 2,
      borderColor: 'info.main',
      bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
    }}
  >
    <HelpOutlineIcon fontSize="small" sx={{ color: 'info.main', mt: 0.25 }} />
    <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="subtitle2">Your first strategy in this project</Typography>
      <Typography variant="caption" color="text.secondary">
        {FIRST_RUN_VOCABULARY}
      </Typography>
    </Box>
    <Button
      variant="text"
      size="small"
      onClick={onDismiss}
      sx={{ textTransform: 'none', color: 'primary.dark', flexShrink: 0 }}
      data-testid={STRATEGY_LIST.wizardFirstRunGotIt}
    >
      Got it
    </Button>
  </Paper>
);

FirstRunNote.displayName = 'FirstRunNote';
